// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AssetAccountingForm } from '@/asset/components/asset-accounting-form';
import { AssetErrorBoundary } from '@/asset/components/asset-error-boundary';
import { CostAdjustmentSection } from '@/asset/components/cost-adjustment-section';
import {
  DepreciationChartSection,
  DepreciationChartSectionSkeleton,
} from '@/asset/components/depreciation-chart-section';
import { UsefulLifeRevisionSection } from '@/asset/components/useful-life-revision-section';
import { useAsset } from '@/asset/hooks/use-asset';
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { Box, Skeleton, Stack } from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import { Suspense } from 'react';

export const Route = createFileRoute('/_app/assets/$assetKey/accounting')({
  component: AssetAccountingRoute,
});

function AssetAccountingRoute() {
  const { assetKey } = Route.useParams();

  return (
    <AssetErrorBoundary>
      <Suspense fallback={<AssetAccountingRouteSkeleton />}>
        <AssetAccountingContent assetKey={assetKey} />
      </Suspense>
    </AssetErrorBoundary>
  );
}

function AssetAccountingContent(props: { assetKey: string }) {
  const { asset } = useAsset(props.assetKey);
  const currentDate = DateTime.now().startOf('day');

  return (
    <AssetAccountingForm
      asset={asset}
      sx={{ paddingX: 2, paddingTop: 3, paddingBottom: 2 }}
      footer={
        <Box
          sx={{
            maxWidth: '100%',
            minWidth: 0,
            overflow: 'hidden',
            px: 2,
            pb: 2,
          }}
        >
          <DepreciationChartSection asset={asset} currentDate={currentDate} />
          <CostAdjustmentSection
            adjustments={asset.accounting.costAdjustments}
            assetKey={props.assetKey}
            capitalizedCosts={asset.accounting.capitalizedCosts}
            costCurrencyCode={asset.accounting.costCurrencyCode}
          />
          <UsefulLifeRevisionSection
            assetKey={props.assetKey}
            revisions={asset.accounting.usefulLifeRevisions}
          />
        </Box>
      }
    />
  );
}

function AssetAccountingRouteSkeleton() {
  return (
    <HardfinTabCardWrapper title="Accounting">
      <Stack spacing={2} sx={{ px: 2, pt: 3, pb: 2 }}>
        <Skeleton height={56} variant="rounded" width="100%" />
        <Skeleton height={56} variant="rounded" width="100%" />
        <Skeleton height={56} variant="rounded" width="100%" />
        <DepreciationChartSectionSkeleton />
      </Stack>
    </HardfinTabCardWrapper>
  );
}
